/**
 * @description Renders the list of knowledge entries
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import { darken } from '@mui/system';

import IFrame from './IFrame';

const styles = {
  container: {
    display: 'grid',
    flexWrap: 'wrap',
    gap: 4,
    gridTemplateColumns: 'repeat(auto-fill, minmax(31.25rem, 1fr))',
    width: '100%'
  },
  knowledgeEntry: {
    maxWidth: '40rem',
    paddingTop: '100%',
    position: 'relative',
    width: '100%',

    '& > .button': {
      visibility: 'hidden'
    },

    '&:hover > .button': {
      visibility: 'visible'
    }
  },
  iframeContainer: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',

    '& > *': {
      border: 0,
      height: '100%',
      width: '100%',
      zIndex: 1
    }
  },
  deleteButton: {
    backgroundColor: 'primary.main',
    bottom: '-1.25rem',
    color: 'primary.contrastText',
    position: 'absolute',
    right: '-1.25rem',
    zIndex: 2,

    '&:hover': {
      backgroundColor: (theme) => darken(theme.palette.primary.main, 0.25)
    }
  },
  skeleton: {
    borderRadius: 0,
    transform: 'none'
  }
};

/**
 * @prop {boolean} canAdmin - indicates if the user can administrate this page or not
 * @prop {boolean} isLoading - indicates if the page is loading or not
 * @prop {{ id: string, embedSrc: string }[]} list - a list of knowledge entries to be rendered
 * @prop {(data: object) => void} onEdit - callback function when the entry's edit button is clicked
 * @prop {(data: object) => void} onDelete - callback function when the entry's delete button is clicked
 */
const KnowledgeList = ({
  canAdmin,
  isLoading,
  list,
  onDelete,
  onEdit
}) => {
  // Prepare a list of actions for each knowledge entry
  const actions = [
    { icon: <DeleteIcon />, name: 'Delete', onClick: onDelete },
    { icon: <EditIcon />, name: 'Edit', onClick: onEdit }
  ];

  // If the list is loading, display a loading overlay
  if (isLoading) {
    return (
      <Box sx={styles.container} style={{ marginBottom: '1rem' }}>
        <Paper elevation={2} sx={styles.knowledgeEntry}>
          <Box sx={styles.iframeContainer}>
            <Skeleton animation='wave' width='100%' height='100%' sx={styles.skeleton} />
          </Box>
        </Paper>
        <Paper elevation={2} sx={styles.knowledgeEntry}>
          <Box sx={styles.iframeContainer}>
            <Skeleton animation='wave' width='100%' height='100%' sx={styles.skeleton} />
          </Box>
        </Paper>
        <Paper elevation={2} sx={styles.knowledgeEntry}>
          <Box sx={styles.iframeContainer}>
            <Skeleton animation='wave' width='100%' height='100%' sx={styles.skeleton} />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={styles.container} style={{ marginBottom: '1rem' }}>
      {list?.map(({ id, embedSrc }) => (
        <Paper key={id} elevation={2} sx={styles.knowledgeEntry}>
          <Box sx={styles.iframeContainer}>
            <IFrame title={`knowledge-entry-${ id }`} src={embedSrc} allowFullScreen frameBorder='0' />
          </Box>
          {canAdmin && (
            <SpeedDial
              ariaLabel='Administrate Knowledge Center Entry'
              className='button'
              icon={<SpeedDialIcon />}
              sx={{ position: 'absolute', bottom: -24, right: -24 }}
            >
              {actions.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  onClick={() => action.onClick(id)}
                  tooltipTitle={action.name}
                />
              ))}
            </SpeedDial>
          )}
        </Paper>
      ))}
    </Box>
  );
};

KnowledgeList.propTypes = {
  canAdmin: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      embedSrc: PropTypes.string.isRequired
    })
  ),
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

KnowledgeList.defaultProps = {
  list: null
};

export default KnowledgeList;
